import { useContext, useEffect, useMemo, useState } from 'react';
import usePersistentFilters from 'hooks/usePersistentFilters';
import { AuthContext } from 'authentication/AuthContext';

const defaultStatus = { label: 'All On', value: 'Allon' };

const getInitialPersistedFilters = defaultAffiliate => ({
  affiliate: defaultAffiliate,
  location: [],
  product: [],
  status: defaultStatus,
  supplier: [],
});

const getInitialFilters = defaultAffiliate => ({
  ...getInitialPersistedFilters(defaultAffiliate),
  search: '',
});

const normalizePersistedFilters = (filters, defaultAffiliate = null) => {
  const initialPersistedFilters = getInitialPersistedFilters(defaultAffiliate);

  return {
    affiliate: filters?.affiliate || initialPersistedFilters.affiliate,
    location: Array.isArray(filters?.location) ? filters.location : [],
    product: Array.isArray(filters?.product) ? filters.product : [],
    status: filters?.status || defaultStatus,
    supplier: Array.isArray(filters?.supplier) ? filters.supplier : [],
  };
};

const normalizeFilters = (filters, defaultAffiliate = null) => ({
  ...getInitialFilters(defaultAffiliate),
  ...filters,
  location: Array.isArray(filters?.location) ? filters.location : [],
  product: Array.isArray(filters?.product) ? filters.product : [],
  search: filters?.search || '',
  status: filters?.status || defaultStatus,
  supplier: Array.isArray(filters?.supplier) ? filters.supplier : [],
});

/**
 * Custom hook to manage supplier authorization filter state
 * Implements draft/applied pattern for better UX
 */
const useSupplierAuthFilters = () => {
  const { affiliates = [] } = useContext(AuthContext);
  const defaultAffiliate = affiliates[0] || null;
  const initialPersistedFilters = useMemo(
    () => getInitialPersistedFilters(defaultAffiliate),
    [defaultAffiliate]
  );

  const initialFilters = useMemo(() => getInitialFilters(defaultAffiliate), [defaultAffiliate]);

  // Persist only non-search filters in local storage.
  const [persistedFilters, setPersistedFilters] = usePersistentFilters(
    'supplierAuthorizationsFilters',
    initialPersistedFilters
  );

  // Applied filter state (what is actually used in API queries)
  const [appliedFilters, setAppliedFilters] = useState(
    normalizeFilters(persistedFilters, defaultAffiliate)
  );

  // Draft filter state (what user is currently editing in the UI)
  const [draftFilters, setDraftFilters] = useState(
    normalizeFilters(persistedFilters, defaultAffiliate)
  );

  const normalizedInitialFilters = useMemo(
    () => normalizeFilters(initialFilters, defaultAffiliate),
    [defaultAffiliate, initialFilters]
  );

  useEffect(() => {
    if (!defaultAffiliate) {
      return;
    }

    setDraftFilters(prev =>
      prev?.affiliate ? normalizeFilters(prev, defaultAffiliate) : normalizedInitialFilters
    );

    setAppliedFilters(prev =>
      prev?.affiliate ? normalizeFilters(prev, defaultAffiliate) : normalizedInitialFilters
    );

    setPersistedFilters(prev =>
      prev?.affiliate
        ? normalizePersistedFilters(prev, defaultAffiliate)
        : normalizePersistedFilters(normalizedInitialFilters, defaultAffiliate)
    );
  }, [defaultAffiliate, normalizedInitialFilters, setPersistedFilters]);

  // Check if there are unsaved filter changes
  const hasChanges = useMemo(
    () => JSON.stringify(draftFilters) !== JSON.stringify(appliedFilters),
    [draftFilters, appliedFilters]
  );

  const isOnDefaultState = useMemo(() => {
    const isResetState = filters => {
      const normalizedFilters = normalizeFilters(filters, defaultAffiliate);

      return (
        !normalizedFilters.location.length &&
        !normalizedFilters.product.length &&
        !normalizedFilters.search &&
        normalizedFilters.status?.value === defaultStatus.value &&
        !normalizedFilters.supplier.length
      );
    };

    return isResetState(draftFilters) && isResetState(appliedFilters);
  }, [appliedFilters, defaultAffiliate, draftFilters]);

  // Handle applying filters
  const handleApply = () => {
    const normalizedDraft = normalizeFilters(draftFilters, defaultAffiliate);

    setAppliedFilters(normalizedDraft);
    setPersistedFilters(normalizePersistedFilters(normalizedDraft, defaultAffiliate));
  };

  // Handle resetting filters
  const handleReset = () => {
    const selectedAffiliate =
      draftFilters?.affiliate || appliedFilters?.affiliate || defaultAffiliate;
    const resetFilters = normalizeFilters(
      {
        ...getInitialFilters(selectedAffiliate || defaultAffiliate),
        affiliate: selectedAffiliate || defaultAffiliate,
      },
      defaultAffiliate
    );

    setDraftFilters(resetFilters);
    setAppliedFilters(resetFilters);
    setPersistedFilters(normalizePersistedFilters(resetFilters, defaultAffiliate));
  };

  const setAffiliateFilter = value => {
    const resetState = {
      affiliate: value,
      location: [],
      product: [],
      status: defaultStatus,
      supplier: [],
    };
    setDraftFilters({ ...resetState, search: '' });
    setAppliedFilters({ ...resetState, search: '' });
    setPersistedFilters(normalizePersistedFilters(resetState, defaultAffiliate));
  };
  const setLocationFilter = value => setDraftFilters(prev => ({ ...prev, location: value }));
  const setSearch = value => setDraftFilters(prev => ({ ...prev, search: value }));
  const setSupplierFilter = value => setDraftFilters(prev => ({ ...prev, supplier: value }));
  const setProductFilter = value => setDraftFilters(prev => ({ ...prev, product: value }));
  const setStatusFilter = value =>
    setDraftFilters(prev => ({
      ...prev,
      status: value || defaultStatus,
    }));

  return {
    // Applied state (for API queries)
    appliedFilters,
    // Draft state (for form inputs)
    draftFilters,

    // Actions
    handleApply,
    handleReset,
    hasChanges,
    isOnDefaultState,
    setAffiliateFilter,
    setLocationFilter,
    setProductFilter,
    setSearch,
    setStatusFilter,
    setSupplierFilter,
  };
};

export default useSupplierAuthFilters;
